package br.com.vvrefeicoes.portal.feature.auth.forgotPassword

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.model.AppError
import br.com.vvrefeicoes.portal.core.model.AppResult
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.auth_forgot_password_email_error
import portalvv.shared.generated.resources.auth_network_error
import portalvv.shared.generated.resources.auth_password_mismatch_error
import portalvv.shared.generated.resources.auth_password_requirements_error
import portalvv.shared.generated.resources.auth_reset_code_expired_error
import portalvv.shared.generated.resources.auth_reset_code_failed_error
import portalvv.shared.generated.resources.auth_reset_code_invalid_error
import portalvv.shared.generated.resources.auth_reset_failed_error
import portalvv.shared.generated.resources.auth_reset_session_expired_error
import portalvv.shared.generated.resources.auth_reset_too_many_attempts_error

enum class ForgotPasswordStep {
    Email,
    Code,
    Password,
}

enum class ForgotPasswordError(
    val message: StringResource,
) {
    EmailFailed(Res.string.auth_forgot_password_email_error),
    Network(Res.string.auth_network_error),
    InvalidCode(Res.string.auth_reset_code_invalid_error),
    CodeExpired(Res.string.auth_reset_code_expired_error),
    TooManyAttempts(Res.string.auth_reset_too_many_attempts_error),
    CodeFailed(Res.string.auth_reset_code_failed_error),
    Requirements(Res.string.auth_password_requirements_error),
    Mismatch(Res.string.auth_password_mismatch_error),
    SessionExpired(Res.string.auth_reset_session_expired_error),
    ResetFailed(Res.string.auth_reset_failed_error),
}

class ForgotPasswordController(
    private val authRepository: AuthRepository,
    private val scope: CoroutineScope,
    private val onBack: () -> Unit,
    private val onPasswordChanged: () -> Unit,
    initialEmail: String,
) {
    var step by mutableStateOf(ForgotPasswordStep.Email)
    var email by mutableStateOf(initialEmail)
    var code by mutableStateOf<List<Char?>>(List(CODE_LENGTH) { null })
    var password by mutableStateOf("")
    var confirmPassword by mutableStateOf("")
    var loading by mutableStateOf(false)
    var error by mutableStateOf<ForgotPasswordError?>(null)

    private var resetToken: String? = null

    val passwordRulesSatisfied: Boolean
        get() = PasswordRule.allSatisfiedBy(password)

    val passwordsMatch: Boolean
        get() = password.isNotEmpty() && password == confirmPassword

    val codeComplete: Boolean
        get() = code.all { it != null }

    fun submitEmail() {
        if (loading) return
        scope.launch {
            loading = true
            error = null
            when (authRepository.requestToken(email)) {
                is AppResult.Success -> step = ForgotPasswordStep.Code
                is AppResult.Failure -> error = ForgotPasswordError.EmailFailed
            }
            loading = false
        }
    }

    fun submitCode() {
        if (!codeComplete || loading) return
        scope.launch {
            loading = true
            error = null
            when (val result = authRepository.verifyResetCode(email, code.joinToString(""))) {
                is AppResult.Success -> {
                    resetToken = result.value
                    step = ForgotPasswordStep.Password
                }
                is AppResult.Failure ->
                    when (result.error) {
                        AppError.Unauthorized -> error = ForgotPasswordError.InvalidCode
                        AppError.Network -> error = ForgotPasswordError.Network
                        // O código não serve mais: só resta pedir outro.
                        AppError.Expired -> restart(ForgotPasswordError.CodeExpired)
                        AppError.TooManyAttempts -> restart(ForgotPasswordError.TooManyAttempts)
                        else -> error = ForgotPasswordError.CodeFailed
                    }
            }
            loading = false
        }
    }

    fun submitPassword() {
        if (loading) return
        error =
            when {
                !passwordRulesSatisfied -> ForgotPasswordError.Requirements
                !passwordsMatch -> ForgotPasswordError.Mismatch
                else -> null
            }
        val token = resetToken
        if (error != null || token == null) return
        scope.launch {
            loading = true
            when (val result = authRepository.resetPassword(token, password)) {
                is AppResult.Success -> onPasswordChanged()
                is AppResult.Failure ->
                    when (result.error) {
                        AppError.Unauthorized, AppError.Expired -> restart(ForgotPasswordError.SessionExpired)
                        AppError.Network -> error = ForgotPasswordError.Network
                        else -> error = ForgotPasswordError.ResetFailed
                    }
            }
            loading = false
        }
    }

    private fun restart(reason: ForgotPasswordError) {
        step = ForgotPasswordStep.Email
        code = List(CODE_LENGTH) { null }
        resetToken = null
        error = reason
    }

    fun back() = onBack()

    companion object {
        const val CODE_LENGTH = 6
    }
}

@Composable
fun rememberForgotPasswordController(
    onBack: () -> Unit,
    onPasswordChanged: () -> Unit,
    initialEmail: String = "",
): ForgotPasswordController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember(initialEmail) {
        ForgotPasswordController(appGraph.authRepository, scope, onBack, onPasswordChanged, initialEmail)
    }
}
